import React from "react";

import {
  TableCellData,
  CellChangeParams,
  ButtonClickParams,
  TableCellType,
} from "./types";
import { TitleCell } from "./cells/TitleCell";
import { FixedCell } from "./cells/FixedCell";
import { InputCell } from "./cells/InputCell";
import { DropdownCell } from "./cells/DropdownCell";
import { ImageCell } from "./cells/ImageCell";
import { MathJaxCell } from "./cells/MathJaxCell";
import { ButtonCell } from "./cells/ButtonCell";

/* -------------------------------- Types -------------------------------- */

export interface CellRenderContext {
  cellId: string;
  cell: TableCellData;
  rowIndex: number;
  colIndex: number;
  isCorrect: boolean;
  isPrediction?: boolean;
  isSubmitted?: boolean;
  striped?: boolean;
  onCellChange: (params: Omit<CellChangeParams, "componentID">) => void;
  onButtonClick: (params: Omit<ButtonClickParams, "componentID">) => void;
}

type CellRenderFunction = (context: CellRenderContext) => React.ReactNode;

/* -------------------------------- Cell Renderers -------------------------------- */

const renderTitleCell: CellRenderFunction = ({ cellId, cell }) => (
  <TitleCell
    key={cellId}
    cellId={cellId}
    text={cell.text}
    colspan={cell.colspan}
    bgColor={cell.bgColor}
  />
);

const renderFixedCell: CellRenderFunction = ({
  cellId,
  cell,
  rowIndex,
  striped,
}) => (
  <FixedCell
    key={cellId}
    cellId={cellId}
    text={cell.text}
    colspan={cell.colspan}
    align={cell.align}
    isZebra={striped && rowIndex % 2 === 1}
    bgColor={cell.bgColor}
  />
);

const renderTextAlignedCell: CellRenderFunction = ({
  cellId,
  cell,
  rowIndex,
  striped,
}) => (
  <FixedCell
    key={cellId}
    cellId={cellId}
    text={cell.text}
    colspan={cell.colspan}
    align="center"
    isZebra={striped && rowIndex % 2 === 1}
    bgColor={cell.bgColor}
  />
);

const renderInputCell: CellRenderFunction = ({
  cellId,
  cell,
  rowIndex,
  colIndex,
  isCorrect,
  isPrediction,
  isSubmitted,
  onCellChange,
}) => (
  <InputCell
    key={cellId}
    cellId={cellId}
    rowIndex={rowIndex}
    colIndex={colIndex}
    inputType={cell.inputType}
    placeholder={cell.placeholder}
    value={cell.value}
    disabled={cell.disabled}
    showFeedback={cell.showFeedback && isSubmitted}
    isCorrect={isCorrect}
    feedback={cell.feedback}
    unique={cell.unique}
    accuracy={cell.accuracy}
    colspan={cell.colspan}
    hint={cell.hint}
    isPrediction={isPrediction}
    isSubmitted={isSubmitted}
    onChange={onCellChange}
  />
);

const renderDropdownCell: CellRenderFunction = ({
  cellId,
  cell,
  rowIndex,
  colIndex,
  isCorrect,
  isPrediction,
  isSubmitted,
  onCellChange,
}) => (
  <DropdownCell
    key={cellId}
    cellId={cellId}
    rowIndex={rowIndex}
    colIndex={colIndex}
    placeholder={cell.placeholder}
    options={cell.options}
    value={cell.value}
    disabled={cell.disabled}
    showFeedback={cell.showFeedback && isSubmitted}
    isCorrect={isCorrect}
    feedback={cell.feedback}
    unique={cell.unique}
    colspan={cell.colspan}
    hint={cell.hint}
    isPrediction={isPrediction}
    isSubmitted={isSubmitted}
    onChange={onCellChange}
  />
);

const renderImageCell: CellRenderFunction = ({ cellId, cell }) => (
  <ImageCell
    key={cellId}
    cellId={cellId}
    image={cell.image}
    alt={cell.alt}
    caption={cell.caption}
    colspan={cell.colspan}
  />
);

const renderImageTitleCell: CellRenderFunction = ({ cellId, cell }) => (
  <ImageCell
    key={cellId}
    cellId={cellId}
    image={cell.image}
    alt={cell.alt}
    caption={cell.caption}
    colspan={cell.colspan}
    isTitle
  />
);

const renderMathJaxCell: CellRenderFunction = ({ cellId, cell }) => (
  <MathJaxCell
    key={cellId}
    cellId={cellId}
    text={cell.text}
    colspan={cell.colspan}
  />
);

const renderButtonCell: CellRenderFunction = ({
  cellId,
  cell,
  rowIndex,
  colIndex,
  onButtonClick,
}) => (
  <ButtonCell
    key={cellId}
    cellId={cellId}
    rowIndex={rowIndex}
    colIndex={colIndex}
    colspan={cell.colspan}
    disabled={cell.disabled}
    text={cell.text}
    buttonIcon={cell.buttonIcon}
    buttonAriaLabel={cell.buttonAriaLabel}
    actionId={cell.actionId}
    onButtonClick={onButtonClick}
  />
);

/* -------------------------------- Configuration Map -------------------------------- */

export const cellTypeRenderers: Record<TableCellType, CellRenderFunction> = {
  TITLE: renderTitleCell,
  IMAGE_TITLE: renderImageTitleCell,
  FIXED: renderFixedCell,
  TEXT_ALIGNED: renderTextAlignedCell,
  INPUT: renderInputCell,
  DROPDOWN: renderDropdownCell,
  IMAGE: renderImageCell,
  MATH_JAX: renderMathJaxCell,
  BUTTON: renderButtonCell,
};

/* -------------------------------- Main Render Function -------------------------------- */

export const renderTableCell = (
  context: CellRenderContext
): React.ReactNode => {
  const renderer = cellTypeRenderers[context.cell.type] ?? renderFixedCell;
  return renderer(context);
};
